import { ReportingGroupFilterSwitcher } from "#src/batteries-included-components/Filters/ReportingGroupFilterSwitcher";
import { FilterArea } from "#src/components/FilterArea";
import { DEFAULT_DATE_RANGES } from "#src/hooks/useDateRange";
import {
  DateSelectorInput,
  DropdownInput,
  type StorageKeys,
} from "@validereinc/common-components";
import React from "react";
import { z } from "zod";
import { Measurements } from "@validereinc/domain-controllers";
import { useIsFeatureAvailable } from "#src/contexts/AuthenticatedContext.helpers";

const { PollutantsEmitted, PollutantsEmittedOptions } = Measurements.logic;

export const EmissionsHomeViewFilterAreaSchema = z.object({
  reporting_scenario: z.string().optional(),
  date_range: z.string().optional(),
  pollutant: z.string().optional(),
});
export const EmissionsHomeViewFilterAreaSchemaKeys =
  EmissionsHomeViewFilterAreaSchema.keyof().Enum;
export type EmissionsHomeViewFilterAreaSchemaType = z.infer<
  typeof EmissionsHomeViewFilterAreaSchema
>;

export const EmissionsHomeViewFilterArea = ({
  viewConfigStorageKey,
}: Pick<StorageKeys, "viewConfigStorageKey">) => {
  const [canUseCalculatorResults] = useIsFeatureAvailable({
    featureFlagQuery: "core:home",
    permissionQuery: "calculator_results:read",
  });
  const [canUseReportingGroups] = useIsFeatureAvailable({
    featureFlagQuery: "core:scenarios",
    permissionQuery: "reporting_groups:read",
  });
  return (
    <FilterArea.Root
      storageKey={viewConfigStorageKey}
      defaultValues={{
        [EmissionsHomeViewFilterAreaSchemaKeys.date_range]:
          DEFAULT_DATE_RANGES.currentWholeYear,
        [EmissionsHomeViewFilterAreaSchemaKeys.pollutant]:
          PollutantsEmitted.co2_eq.id,
      }}
      applyDefaultValues
    >
      <FilterArea.Container aria-label="View filters for Emissions Summary data for your company">
        <FilterArea.Content>
          {({ handleOnChange }) => (
            <div style={{ display: "flex", gap: 8 }}>
              {canUseReportingGroups ? (
                <ReportingGroupFilterSwitcher
                  variant="withMenuEdit"
                  name={
                    EmissionsHomeViewFilterAreaSchemaKeys.reporting_scenario
                  }
                  shouldSelectFirstOptionAsDefault
                  onChange={(val) =>
                    handleOnChange(
                      val,
                      EmissionsHomeViewFilterAreaSchemaKeys.reporting_scenario
                    )
                  }
                />
              ) : null}
              {canUseCalculatorResults ? (
                <>
                  <DateSelectorInput
                    name={EmissionsHomeViewFilterAreaSchemaKeys.date_range}
                    variant="month"
                    isRange
                    isInline
                    onChange={(val) =>
                      handleOnChange(
                        val,
                        EmissionsHomeViewFilterAreaSchemaKeys.date_range
                      )
                    }
                  />
                  <DropdownInput
                    name={EmissionsHomeViewFilterAreaSchemaKeys.pollutant}
                    options={PollutantsEmittedOptions}
                    labelKey="label"
                    valueKey="value"
                    isClearable={false}
                    isInline
                    isSortedAlphabetically={false}
                    onChange={(val) =>
                      handleOnChange(
                        val,
                        EmissionsHomeViewFilterAreaSchemaKeys.pollutant
                      )
                    }
                  />
                </>
              ) : null}
            </div>
          )}
        </FilterArea.Content>
      </FilterArea.Container>
    </FilterArea.Root>
  );
};
